import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { FileText, CheckCircle, Calendar as CalendarIcon, Star, Upload, X, File, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

import { API_URL } from '../utils/api';

// Split fields into sections for multi-page navigation
const splitIntoSections = (fields) => {
  const sections = [];
  let current = { title: null, description: null, fields: [] };
  for (const field of fields) {
    if (field.type === 'section') {
      if (current.fields.length > 0 || sections.length === 0) {
        sections.push(current);
      }
      current = { title: field.label, description: field.description, fields: [] };
    } else {
      current.fields.push(field);
    }
  }
  sections.push(current);
  return sections.filter(s => s.fields.length > 0 || s.title);
};

// Validation helper
const validateField = (field, answer) => {
  // Required check
  if (field.required) {
    if (field.type === 'checkbox') {
      if (!answer || answer.length === 0) return 'This field is required';
    } else if (field.type === 'file') {
      // Multi-file: at least one file required
      if (Array.isArray(answer)) {
        if (answer.length === 0) return 'Please upload at least one file';
      } else if (!answer || !answer.file_id) {
        return 'Please upload at least one file';
      }
    } else if (field.type === 'multiple_choice_grid' || field.type === 'checkbox_grid') {
      if (!answer || typeof answer !== 'object') return 'This field is required';
      const rows = field.rows || [];
      for (const row of rows) {
        const rowVal = answer[row];
        if (field.type === 'checkbox_grid') {
          if (!rowVal || rowVal.length === 0) return `Please fill in row: ${row}`;
        } else {
          if (!rowVal) return `Please select an option for: ${row}`;
        }
      }
    } else if (!answer || answer === '') {
      return 'This field is required';
    }
  }

  // Custom validation
  const v = field.validation;
  if (!v || v.type === 'none') return null;
  const msg = v.message;

  if (v.type === 'length' && answer) {
    const len = String(answer).length;
    if (v.min && len < v.min) return msg || `Must be at least ${v.min} characters`;
    if (v.max && len > v.max) return msg || `Must be at most ${v.max} characters`;
  }
  if (v.type === 'regex' && answer && v.pattern) {
    try { if (!new RegExp(v.pattern).test(answer)) return msg || 'Invalid format'; } catch { /* ignore bad regex */ }
  }
  if (v.type === 'email_format' && answer) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(answer)) return msg || 'Enter a valid email address';
  }
  if (v.type === 'url_format' && answer) {
    if (!/^https?:\/\/.+\..+/.test(answer)) return msg || 'Enter a valid URL';
  }
  if (v.type === 'number_format' && answer) {
    if (!/\d/.test(answer)) return msg || 'Must contain a number';
  }
  if (v.type === 'range' && answer !== '' && answer !== undefined) {
    const num = parseFloat(answer);
    if (isNaN(num)) return msg || 'Must be a number';
    if (v.min !== undefined && num < v.min) return msg || `Must be at least ${v.min}`;
    if (v.max !== undefined && num > v.max) return msg || `Must be at most ${v.max}`;
  }
  if (v.type === 'select_count' && Array.isArray(answer)) {
    if (v.min && answer.length < v.min) return msg || `Select at least ${v.min}`;
    if (v.max && answer.length > v.max) return msg || `Select at most ${v.max}`;
  }
  return null;
};

const PublicFormPage = () => {
  const { formId } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [uploadingFields, setUploadingFields] = useState({});
  const [currentSection, setCurrentSection] = useState(0);
  const [respondentEmail, setRespondentEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [quizResult, setQuizResult] = useState(null);

  const fetchForm = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/forms/public/${formId}`);
      setForm(response.data);
      const initialAnswers = {};
      response.data.fields.forEach(field => {
        if (field.type === 'checkbox') initialAnswers[field.id] = [];
        else if (field.type === 'multiple_choice_grid' || field.type === 'checkbox_grid') initialAnswers[field.id] = {};
        else initialAnswers[field.id] = '';
      });
      setAnswers(initialAnswers);
    } catch {
      toast.error('Form not found or inactive');
    } finally {
      setLoading(false);
    }
  }, [formId]);

  useEffect(() => { fetchForm(); }, [fetchForm]);

  // Quiz anti-cheat: block Ctrl+C, Ctrl+A, Ctrl+U
  useEffect(() => {
    if (!form?.is_quiz) return;
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && ['c', 'a', 'u', 'C', 'A', 'U'].includes(e.key)) {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [form?.is_quiz]);

  const sections = form ? splitIntoSections(form.fields) : [];
  const hasSections = sections.length > 1;

  // Evaluate conditional logic for a field
  const isFieldVisible = (field) => {
    if (!field.conditional_logic) return true;
    const { field_id, operator, value } = field.conditional_logic;
    if (!field_id || !value) return true;
    const answer = answers[field_id];
    if (answer === undefined || answer === null) return false;
    const ansStr = Array.isArray(answer) ? answer : [String(answer)];
    const valStr = String(value);
    switch (operator) {
      case 'equals': return Array.isArray(answer) ? answer.includes(valStr) : String(answer) === valStr;
      case 'not_equals': return Array.isArray(answer) ? !answer.includes(valStr) : String(answer) !== valStr;
      case 'contains': return ansStr.some(a => String(a).toLowerCase().includes(valStr.toLowerCase()));
      default: return true;
    }
  };

  const handleAnswerChange = (fieldId, value) => {
    setAnswers(prev => ({ ...prev, [fieldId]: value }));
    setErrors(prev => ({ ...prev, [fieldId]: null }));
  };

  const handleCheckboxChange = (fieldId, optionValue, checked) => {
    setAnswers(prev => {
      const current = prev[fieldId] || [];
      return { ...prev, [fieldId]: checked ? [...current, optionValue] : current.filter(v => v !== optionValue) };
    });
    setErrors(prev => ({ ...prev, [fieldId]: null }));
  };

  const handleGridChange = (fieldId, row, value, isCheckbox) => {
    setAnswers(prev => {
      const grid = { ...(prev[fieldId] || {}) };
      if (isCheckbox) {
        const current = grid[row] || [];
        grid[row] = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      } else {
        grid[row] = value;
      }
      return { ...prev, [fieldId]: grid };
    });
    setErrors(prev => ({ ...prev, [fieldId]: null }));
  };

  const handleFileUpload = async (fieldId, files) => {
    if (!files || files.length === 0) return;
    const maxSize = 50 * 1024 * 1024; // 50MB
    const fileList = Array.from(files);
    
    // Validate all files first
    for (const file of fileList) {
      if (file.size > maxSize) { toast.error(`"${file.name}" exceeds 50MB limit`); return; }
    }
    
    setUploadingFields(prev => ({ ...prev, [fieldId]: true }));
    try {
      // Upload all files in parallel
      const uploads = fileList.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API_URL}/api/files/upload?form_id=${formId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
        return { file_id: response.data.file_id, filename: response.data.filename, size: response.data.size, content_type: response.data.content_type };
      });
      const newFiles = await Promise.all(uploads);
      
      setAnswers(prev => {
        const existing = prev[fieldId];
        if (Array.isArray(existing)) {
          return { ...prev, [fieldId]: [...existing, ...newFiles] };
        } else if (existing && existing.file_id) {
          return { ...prev, [fieldId]: [existing, ...newFiles] };
        }
        return { ...prev, [fieldId]: newFiles };
      });
      setErrors(prev => ({ ...prev, [fieldId]: null }));
      toast.success(`${newFiles.length} file${newFiles.length > 1 ? 's' : ''} uploaded`);
    } catch { toast.error('Failed to upload files'); }
    finally { setUploadingFields(prev => ({ ...prev, [fieldId]: false })); }
  };

  const handleRemoveFile = (fieldId, fileIndex) => {
    setAnswers(prev => {
      const existing = prev[fieldId];
      if (Array.isArray(existing)) {
        const updated = existing.filter((_, i) => i !== fileIndex);
        return { ...prev, [fieldId]: updated.length > 0 ? updated : '' };
      }
      return { ...prev, [fieldId]: '' };
    });
  };

  const validateSection = (sectionIndex) => {
    const section = sections[sectionIndex];
    if (!section) return true;
    const newErrors = {};
    section.fields.forEach(field => {
      const err = validateField(field, answers[field.id]);
      if (err) newErrors[field.id] = err;
    });
    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const validateAll = () => {
    const newErrors = {};
    form.fields.forEach(field => {
      if (field.type === 'section') return;
      if (!isFieldVisible(field)) return; // Skip hidden fields
      const err = validateField(field, answers[field.id]);
      if (err) newErrors[field.id] = err;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateSection(currentSection)) {
      toast.error('Please fix the errors before continuing');
      return;
    }
    
    // Check if any multiple_choice or dropdown field in current section has go_to_section
    const section = sections[currentSection];
    if (section) {
      for (const field of section.fields) {
        if (field.type === 'multiple_choice' || field.type === 'dropdown') {
          const selectedValue = answers[field.id];
          if (selectedValue) {
            const selectedOption = field.options?.find(o => o.value === selectedValue);
            if (selectedOption?.go_to_section === 'submit') {
              // Submit the form directly
              doSubmit();
              return;
            }
            if (selectedOption?.go_to_section != null && selectedOption.go_to_section !== '') {
              const targetIndex = parseInt(selectedOption.go_to_section, 10);
              if (!isNaN(targetIndex) && targetIndex >= 0 && targetIndex < sections.length) {
                setCurrentSection(targetIndex);
                return;
              }
            }
          }
        }
      }
    }
    
    // Default: go to next section
    setCurrentSection(prev => Math.min(prev + 1, sections.length - 1));
  };

  const handlePrev = () => setCurrentSection(prev => Math.max(prev - 1, 0));

  const doSubmit = async () => {
    if (!validateAll()) { toast.error('Please fill in all required fields'); return; }
    // Validate email if collect_email is enabled
    if (form?.collect_email) {
      if (!respondentEmail) {
        setEmailError('Email address is required');
        toast.error('Please enter your email address');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(respondentEmail)) {
        setEmailError('Please enter a valid email address');
        toast.error('Please enter a valid email address');
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = { answers };
      if (form?.collect_email && respondentEmail) {
        payload.respondent_email = respondentEmail;
      }
      const response = await axios.post(`${API_URL}/api/responses/${formId}`, payload);
      if (response.data.quiz_result) {
        setQuizResult(response.data.quiz_result);
      }
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit response');
    }
    finally { setSubmitting(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Block native form submit if not on last section
    if (hasSections && currentSection < sections.length - 1) return;
    await doSubmit();
  };

  // Prevent Enter key from submitting the form
  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
    }
  };

  const handleSubmitAnother = () => {
    setSubmitted(false);
    setCurrentSection(0);
    const initialAnswers = {};
    form.fields.forEach(field => {
      if (field.type === 'checkbox') initialAnswers[field.id] = [];
      else if (field.type === 'multiple_choice_grid' || field.type === 'checkbox_grid') initialAnswers[field.id] = {};
      else initialAnswers[field.id] = '';
    });
    setAnswers(initialAnswers);
  };

  if (loading) return <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;

  if (!form) return (
    <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center">
      <Card className="max-w-md"><CardContent className="p-8 text-center"><FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" /><h2 className="text-xl font-semibold font-['Outfit'] text-slate-900 mb-2">Form Not Found</h2><p className="text-slate-500">This form may have been deleted or is no longer active.</p></CardContent></Card>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-4" data-testid="form-submitted">
      <div className="max-w-lg w-full space-y-4">
        {/* Main success card */}
        <Card className="w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-8 h-8 text-green-600" /></div>
            <h2 className="text-2xl font-semibold font-['Outfit'] text-slate-900 mb-2">Response Submitted!</h2>
            <p className="text-slate-500 mb-6">Thank you for your response. It has been recorded successfully.</p>
            
            {/* Quiz Score */}
            {quizResult && quizResult.release_marks === 'immediate' && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6 text-left" data-testid="quiz-score-card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-amber-800 font-['Outfit']">Your Score</h3>
                  {quizResult.show_point_values && (
                    <span className="text-2xl font-bold text-amber-600">
                      {quizResult.score} / {quizResult.total}
                    </span>
                  )}
                </div>
                {quizResult.show_point_values && (
                  <div className="w-full bg-amber-100 rounded-full h-3 mb-4">
                    <div className="bg-amber-500 h-3 rounded-full transition-all" style={{ width: `${quizResult.total > 0 ? (quizResult.score / quizResult.total) * 100 : 0}%` }}></div>
                  </div>
                )}
                
                {/* Per-question results */}
                {quizResult.show_missed_questions && quizResult.field_results && (
                  <div className="space-y-2 mt-4">
                    {quizResult.field_results.map((fr, idx) => {
                      const fieldDef = form.fields.find(f => f.id === fr.field_id);
                      if (!fieldDef) return null;
                      return (
                        <div key={fr.field_id} className={`flex items-center justify-between p-2 rounded-lg ${fr.is_correct ? 'bg-green-50' : 'bg-red-50'}`}>
                          <div className="flex items-center gap-2">
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium ${fr.is_correct ? 'bg-green-200 text-green-700' : 'bg-red-200 text-red-700'}`}>
                              {fr.is_correct ? '✓' : '✗'}
                            </span>
                            <span className="text-sm text-slate-700 truncate max-w-[200px]">{fieldDef.label}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {quizResult.show_correct_answers && !fr.is_correct && quizResult.correct_answers && (
                              <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded">
                                Ans: {Array.isArray(quizResult.correct_answers[fr.field_id]) ? quizResult.correct_answers[fr.field_id].join(', ') : quizResult.correct_answers[fr.field_id]}
                              </span>
                            )}
                            {quizResult.show_point_values && (
                              <span className={`text-xs font-medium ${fr.is_correct ? 'text-green-700' : 'text-red-600'}`}>
                                {fr.points_earned}/{fr.points_possible}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            
            {quizResult && quizResult.release_marks !== 'immediate' && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 text-left">
                <p className="text-sm text-blue-700">Your marks will be released after manual review by the form owner.</p>
              </div>
            )}
            
            <Button onClick={handleSubmitAnother} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-another-btn">Submit Another Response</Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const activeSection = sections[currentSection];
  const fieldsToRender = hasSections ? activeSection?.fields || [] : form.fields.filter(f => f.type !== 'section');

  return (
    <div className={`min-h-screen bg-[#F4F4F5] py-8 px-4 ${form?.is_quiz ? 'select-none' : ''}`} data-testid="public-form-page"
      {...(form?.is_quiz ? {
        onContextMenu: (e) => e.preventDefault(),
        onCopy: (e) => e.preventDefault(),
        onCut: (e) => e.preventDefault(),
      } : {})}
    >
      <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="max-w-3xl mx-auto">
        {/* Form Header */}
        <Card className="mb-4 border-slate-200 shadow-sm overflow-hidden" data-testid="public-form-header">
          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-indigo-600 rounded-l-xl"></div>
          <CardContent className="p-6 pl-8">
            <h1 className="text-2xl font-semibold font-['Outfit'] text-slate-900 mb-2">{form.title}</h1>
            {form.description && <p className="text-slate-500">{form.description}</p>}
            {hasSections && (
              <div className="flex items-center gap-2 mt-3">
                {sections.map((_, i) => (
                  <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= currentSection ? 'bg-indigo-600' : 'bg-slate-200'}`} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Collect Email */}
        {form.collect_email && currentSection === 0 && (
          <Card className="mb-4 border-slate-200 shadow-sm" data-testid="email-collection-card">
            <CardContent className="p-6">
              <Label className="text-base font-medium text-slate-900 mb-4 block">
                Email address<span className="text-red-500 ml-1">*</span>
              </Label>
              <p className="text-sm text-slate-500 mb-3">Your email will be recorded with this response</p>
              <Input
                type="email"
                value={respondentEmail}
                onChange={(e) => { setRespondentEmail(e.target.value); setEmailError(''); }}
                placeholder="your@email.com"
                className={`max-w-md ${emailError ? 'border-red-500' : ''}`}
                data-testid="respondent-email-input"
              />
              {emailError && <p className="text-sm text-red-500 mt-2">{emailError}</p>}
            </CardContent>
          </Card>
        )}

        {/* Section Title */}
        {hasSections && activeSection?.title && (
          <Card className="mb-4 border-slate-200 shadow-sm border-t-4 border-t-indigo-500">
            <CardContent className="p-5">
              <p className="text-lg font-semibold text-slate-900">{activeSection.title}</p>
              {activeSection.description && <p className="text-sm text-slate-500 mt-1">{activeSection.description}</p>}
            </CardContent>
          </Card>
        )}

        {/* Fields */}
        {fieldsToRender.map((field) => {
          if (!isFieldVisible(field)) return null;
          return (
            <Card key={field.id} className="mb-4 border-slate-200 shadow-sm" data-testid={`public-field-${field.id}`}>
              <CardContent className="p-6">
                <Label className="text-base font-medium text-slate-900 mb-4 block">
                  {field.label}{field.required && <span className="text-red-500 ml-1">*</span>}
                </Label>
                {field.description && <p className="text-sm text-slate-500 mb-4">{field.description}</p>}
                <div className="mt-2">
                  <FieldInput field={field} answers={answers} onChange={handleAnswerChange} onCheckbox={handleCheckboxChange} onGrid={handleGridChange} onFileUpload={handleFileUpload} onFileRemove={handleRemoveFile} uploading={uploadingFields} formId={formId} />
                </div>
                {errors[field.id] && <p className="text-sm text-red-500 mt-2" data-testid={`error-${field.id}`}>{errors[field.id]}</p>}
              </CardContent>
            </Card>
          );
        })}

        {/* Navigation / Submit */}
        <div className="flex justify-between items-center mt-6">
          <div className="flex gap-2">
            {hasSections && currentSection > 0 && (
              <Button type="button" variant="outline" onClick={handlePrev} data-testid="prev-section-btn"><ChevronLeft className="w-4 h-4 mr-1" />Back</Button>
            )}
          </div>
          <div className="flex gap-2 items-center">
            <p className="text-sm text-slate-400 mr-2">Never submit passwords through forms.</p>
            {hasSections && currentSection < sections.length - 1 ? (
              <Button type="button" onClick={handleNext} className="bg-indigo-600 hover:bg-indigo-700" data-testid="next-section-btn">Next<ChevronRight className="w-4 h-4 ml-1" /></Button>
            ) : (
              <Button type="button" onClick={doSubmit} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-form-btn">{submitting ? 'Submitting...' : 'Submit'}</Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

// Field Input component
const FieldInput = ({ field, answers, onChange, onCheckbox, onGrid, onFileUpload, onFileRemove, uploading, formId }) => {
  switch (field.type) {
    case 'short_text':
      return <Input value={answers[field.id] || ''} onChange={(e) => onChange(field.id, e.target.value)} placeholder="Your answer" className="border-0 border-b border-slate-300 rounded-none px-0 focus:border-indigo-600 focus-visible:ring-0" data-testid={`input-${field.id}`} />;
    case 'paragraph':
      return <Textarea value={answers[field.id] || ''} onChange={(e) => onChange(field.id, e.target.value)} placeholder="Your answer" rows={4} className="resize-none border-slate-200 focus:border-indigo-600 focus-visible:ring-0" data-testid={`textarea-${field.id}`} />;
    case 'number':
      return <Input type="number" value={answers[field.id] || ''} onChange={(e) => onChange(field.id, e.target.value)} placeholder="Your answer" className="max-w-md border-0 border-b border-slate-300 rounded-none px-0 focus:border-indigo-600 focus-visible:ring-0" data-testid={`input-${field.id}`} />;
    case 'email':
      return <Input type="email" value={answers[field.id] || ''} onChange={(e) => onChange(field.id, e.target.value)} placeholder="name@example.com" className="max-w-md border-0 border-b border-slate-300 rounded-none px-0 focus:border-indigo-600 focus-visible:ring-0" data-testid={`input-${field.id}`} />;
    case 'phone':
      return <Input type="tel" value={answers[field.id] || ''} onChange={(e) => onChange(field.id, e.target.value)} placeholder="+1 (555) 000-0000" className="max-w-md border-0 border-b border-slate-300 rounded-none px-0 focus:border-indigo-600 focus-visible:ring-0" data-testid={`input-${field.id}`} />;
    case 'time':
      return <Input type="time" value={answers[field.id] || ''} onChange={(e) => onChange(field.id, e.target.value)} className="max-w-md" data-testid={`input-${field.id}`} />;
    case 'multiple_choice':
      return (
        <RadioGroup value={answers[field.id] || ''} onValueChange={(value) => onChange(field.id, value)} className="space-y-3" data-testid={`radio-group-${field.id}`}>
          {field.options?.map((option) => (
            <Label key={option.id} htmlFor={option.id} className="flex items-center space-x-3 cursor-pointer p-2 rounded-lg hover:bg-slate-50 transition-colors">
              <RadioGroupItem value={option.value} id={option.id} className="flex-shrink-0" />
              <span className="font-normal">{option.label}</span>
            </Label>
          ))}
        </RadioGroup>
      );
    case 'checkbox':
      return (
        <div className="space-y-3" data-testid={`checkbox-group-${field.id}`}>
          {field.options?.map((option) => (
            <Label key={option.id} htmlFor={option.id} className="flex items-center space-x-3 cursor-pointer p-2 rounded-lg hover:bg-slate-50 transition-colors">
              <Checkbox id={option.id} checked={(answers[field.id] || []).includes(option.value)} onCheckedChange={(checked) => onCheckbox(field.id, option.value, checked)} className="flex-shrink-0" />
              <span className="font-normal">{option.label}</span>
            </Label>
          ))}
        </div>
      );
    case 'dropdown':
      return (
        <Select value={answers[field.id] || ''} onValueChange={(value) => onChange(field.id, value)}>
          <SelectTrigger className="max-w-md" data-testid={`select-${field.id}`}><SelectValue placeholder="Select an option" /></SelectTrigger>
          <SelectContent>{field.options?.map((option) => (<SelectItem key={option.id} value={option.value}>{option.label}</SelectItem>))}</SelectContent>
        </Select>
      );
    case 'date': {
      // Handle both YYYY-MM-DD and ISO datetime formats
      let selectedDate;
      if (answers[field.id]) {
        const val = answers[field.id];
        if (val.length === 10 && val.includes('-')) {
          // YYYY-MM-DD: parse as local date (no timezone shift)
          const [y, m, d] = val.split('-').map(Number);
          selectedDate = new Date(y, m - 1, d);
        } else {
          selectedDate = new Date(val);
        }
      }
      return (
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" className="max-w-md justify-start text-left font-normal" data-testid={`date-picker-${field.id}`}>
              <CalendarIcon className="mr-2 h-4 w-4" />{selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={selectedDate} onSelect={(date) => {
            if (date) {
              // Store as YYYY-MM-DD (local date only, no timezone shift)
              const yyyy = date.getFullYear();
              const mm = String(date.getMonth() + 1).padStart(2, '0');
              const dd = String(date.getDate()).padStart(2, '0');
              onChange(field.id, `${yyyy}-${mm}-${dd}`);
            } else {
              onChange(field.id, '');
            }
          }} initialFocus /></PopoverContent>
        </Popover>
      );
    }
    case 'file': {
      const fileAnswer = answers[field.id];
      const isUploading = uploading[field.id];
      const files = Array.isArray(fileAnswer) ? fileAnswer : (fileAnswer && fileAnswer.file_id ? [fileAnswer] : []);
      return (
        <div className="space-y-3 max-w-md">
          {files.map((f, idx) => (
            <div key={f.file_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`file-uploaded-${field.id}-${idx}`}>
              <File className="w-6 h-6 text-indigo-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{f.filename}</p>
                <p className="text-xs text-slate-500">{(f.size / 1024).toFixed(1)} KB</p>
              </div>
              <button type="button" onClick={() => onFileRemove(field.id, idx)} className="p-1 text-slate-400 hover:text-red-500 rounded"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <label className={`block border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${isUploading ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 hover:border-indigo-300'}`} data-testid={`file-upload-${field.id}`}>
            <input type="file" multiple className="hidden" onChange={(e) => { onFileUpload(field.id, e.target.files); e.target.value = ''; }} disabled={isUploading} accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.json,.zip,.rar" />
            {isUploading ? (<><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div><p className="text-sm text-indigo-600">Uploading...</p></>) : (<><Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" /><p className="text-sm text-slate-600">{files.length > 0 ? 'Add more files' : 'Click to upload or drag and drop'}</p><p className="text-xs text-slate-400 mt-1">Select multiple files - Max 50MB each</p></>)}
          </label>
        </div>
      );
    }
    case 'rating': {
      const ratingValue = parseInt(answers[field.id]) || 0;
      return (
        <div className="flex gap-2" data-testid={`rating-${field.id}`}>
          {[1, 2, 3, 4, 5].map((star) => (
            <button key={star} type="button" onClick={() => onChange(field.id, String(star))} className="focus:outline-none">
              <Star className={`w-8 h-8 transition-colors ${star <= ratingValue ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}`} />
            </button>
          ))}
        </div>
      );
    }
    case 'linear_scale': {
      const minVal = field.min_value || 1;
      const maxVal = field.max_value || 5;
      const scaleValue = answers[field.id] || '';
      return (
        <div className="space-y-2" data-testid={`linear-scale-${field.id}`}>
          <div className="flex items-center justify-between max-w-md">
            {field.min_label && <span className="text-sm text-slate-500">{field.min_label}</span>}
            <div className="flex-1"></div>
            {field.max_label && <span className="text-sm text-slate-500">{field.max_label}</span>}
          </div>
          <div className="flex gap-4">
            {Array.from({ length: maxVal - minVal + 1 }, (_, i) => {
              const value = String(minVal + i);
              return (
                <button key={value} type="button" onClick={() => onChange(field.id, value)}
                  className={`w-10 h-10 rounded-full border-2 flex items-center justify-center text-sm transition-colors ${scaleValue === value ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300 text-slate-600 hover:border-indigo-300'}`}>
                  {value}
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    case 'multiple_choice_grid': {
      const gridAnswers = answers[field.id] || {};
      return (
        <div className="overflow-x-auto" data-testid={`grid-${field.id}`}>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="p-3 text-left"></th>
                {(field.columns || []).map((col, i) => <th key={i} className="p-3 text-center text-slate-600 font-medium min-w-[80px]">{col}</th>)}
              </tr>
            </thead>
            <tbody>
              {(field.rows || []).map((row, ri) => (
                <tr key={ri} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-3 text-slate-700 font-medium">{row}</td>
                  {(field.columns || []).map((col, ci) => (
                    <td key={ci} className="p-3 text-center">
                      <input type="radio" name={`${field.id}-${row}`} checked={gridAnswers[row] === col} onChange={() => onGrid(field.id, row, col, false)}
                        className="w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-500" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case 'checkbox_grid': {
      const gridAnswers = answers[field.id] || {};
      return (
        <div className="overflow-x-auto" data-testid={`grid-${field.id}`}>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="p-3 text-left"></th>
                {(field.columns || []).map((col, i) => <th key={i} className="p-3 text-center text-slate-600 font-medium min-w-[80px]">{col}</th>)}
              </tr>
            </thead>
            <tbody>
              {(field.rows || []).map((row, ri) => (
                <tr key={ri} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-3 text-slate-700 font-medium">{row}</td>
                  {(field.columns || []).map((col, ci) => (
                    <td key={ci} className="p-3 text-center">
                      <input type="checkbox" checked={(gridAnswers[row] || []).includes(col)} onChange={() => onGrid(field.id, row, col, true)}
                        className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    default:
      return null;
  }
};

export default PublicFormPage;
